/**
 * @fileoverview Hook Data/Actions Layer optimizado para gestión de negociaciones (deals) del cliente en Inbox (CustomerFunnelsMC).
 * @module useCustomerFunnelsData
 * @category Hook - Data/Actions Layer
 * @since 3.1.0
 * @version 3.2.0 - JSDoc expandido, flujos y referencias cruzadas
 *
 * @typedef {Object} CustomerDeal
 * @description Objeto negociación (deal) asociada a un cliente.
 * @property {string|number} id - ID único de la negociación
 * @property {Object} customer - Objeto customer relacionado
 * @property {string} [name] - Nombre de la negociación
 * @property {string} [status] - Estado de la negociación (ej: 'open', 'won', 'lost')
 * @property {string} [amount] - Monto de la negociación
 * @property {string} [stage] - Etapa del embudo
 * @property {string} [funnel] - Embudo relacionado
 * @property {Object} [meta] - Metadatos adicionales (ej: fechas, tags)
 *
 * @typedef {Object} UseCustomerFunnelsDataReturn
 * @description Estados y acciones retornados por el hook optimizado
 * @property {CustomerDeal[]} customerDeals - Lista de negociaciones del cliente
 * @property {boolean} loading - Estado de carga de negociaciones
 * @property {function} reloadDeals - Función para recargar negociaciones
 * @property {function} openDealModal - Función para abrir modal de negociación
 * @property {Object} regionalConfig - Configuración regional para visualización
 *
 * @see CustomerFunnelsMC Componente consumidor principal
 * @see useCustomerFunnelsUI Hook UI complementario
 * @see dealsModelMC API de negociaciones
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';
import dealsModelMC from '../../../models/DealsModelMC';
import { LOAD_DATA_FAILURE } from '../../../../actions/inbox/conversationActions';
import { getRegionalConfig } from '../../../../config';
import ensureInboxCustomer from '../../../../services/inbox/ensureInboxCustomerService';
import useSelectedConversation from '../../shared/useSelectedConversation';

/**
 * Hook Data/Actions Layer para cargar, refrescar y abrir modal de negociaciones (deals) del cliente.
 *
 * Implementa el patrón de **Data/Actions Layer** con separación estricta de responsabilidades y optimización avanzada:
 * - ✅ Carga y refresco de negociaciones vía API
 * - ✅ Manejo de loading y errores internacionalizados
 * - ✅ Exposición de función para abrir modal de negociación
 * - ✅ Configuración regional memoizada
 * - ✅ Performance: useCallback, useMemo, useEffect, debug tracking
 *
 * **Flujo de operación:**
 * 1. Al montar o cambiar el customer, carga negociaciones automáticamente
 * 2. Permite recargar negociaciones manualmente (reloadDeals)
 * 3. Permite abrir modal de creación/edición de negociación (openDealModal)
 * 4. Configuración regional se memoiza para visualización
 * 5. Debug tracking de dependencias críticas
 *
 * @function useCustomerFunnelsData
 * @category Hook - Data/Actions Layer
 * @param {Object} customerData - Objeto con datos del customer (customer: {id, ...})
 * @returns {UseCustomerFunnelsDataReturn} Estados y acciones para CustomerFunnelsMC
 *
 * @see {@link CustomerFunnelsMC} Componente consumidor principal
 * @see {@link useCustomerFunnelsUI} Hook UI complementario
 * @see {@link dealsModelMC} API de negociaciones
 * @since 3.1.0
 * @version 3.2.0 - JSDoc expandido y optimización
 *
 * @example
 * const { customerDeals, loading, reloadDeals, openDealModal, regionalConfig } = useCustomerFunnelsData(customerData);
 */

export default function useCustomerFunnelsData(customerData) {
  const dispatch = useDispatch();
  const conversation = useSelectedConversation();
  const { t } = useTranslation('InboxMC');
  const [customerDeals, setCustomerDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const regionalConfig = useMemo(() => getRegionalConfig(), []);

  /**
   * Carga y refresca las negociaciones (deals) del cliente desde la API.
   *
   * @function reloadDeals
   * @memberof module:useCustomerFunnelsData
   * @description
   * **Flujo de operación:**
   * 1. Activa loading
   * 2. Llama a la API para obtener negociaciones del customer
   * 3. Actualiza el estado local con la lista recibida
   * 4. Maneja errores internacionalizados y los despacha a Redux
   * 5. Desactiva loading al finalizar
   *
   * **Validaciones:**
   * - Verifica existencia de customerData.id
   * - Maneja errores y despacha a Redux
   *
   * @throws {Error} Si la API falla, despacha error internacionalizado
   *
   * @returns {Promise<void>} Promesa que resuelve tras refrescar negociaciones
   *
   * @example
   * await reloadDeals();
   */
  const reloadDeals = useCallback(async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const deals = await dealsModelMC.getCustomerDeals(customerData.id);
      setCustomerDeals(deals || []);
    } catch (error) {
      const errorMessage = error?.message || error || t('Funnels.error_loading');
      dispatch({ type: LOAD_DATA_FAILURE, error: errorMessage });
    } finally {
      setLoading(false);
    }
  }, [customerData, dispatch, t]);

  /**
   * Abre el modal lateral para crear o editar una negociación (deal).
   *
   * @function openDealModal
   * @memberof module:useCustomerFunnelsData
   * @param {Object} [deal] - Negociación a editar (opcional)
   * @param {Object} [selectedFunnel] - Embudo seleccionado (opcional)
   * @description
   * **Flujo de operación:**
   * 1. Despacha acción para abrir sidebar con el componente ManageDeal
   * 2. Pasa el deal y funnel seleccionados, y la función reloadDeals para refresco tras guardar
   *
   * **Validaciones:**
   * - Si deal no existe, crea uno nuevo con customerData
   * - Pasa reloadDeals para refresco tras guardar
   *
   * @returns {void}
   *
   * @example
   * openDealModal(); // Para crear nueva
   * openDealModal(deal, funnel); // Para editar existente
   */
  const openDealModal = useCallback(async (deal, selectedFunnel) => {
    if (deal?.id) {
      dispatch({
        type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
        payload: {
          componentKey: 'ManageDeal',
          props: {
            externalCreate: true,
            deal,
            selectedFunnel,
            reloadDeals
          }
        }
      });
      return;
    }
    if (!conversation) return;
    const result = await dispatch(ensureInboxCustomer(conversation));
    if (!result.success) return;
    const customerForDeal = result.customer || customerData;
    dispatch({
      type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
      payload: {
        componentKey: 'ManageDeal',
        props: {
          externalCreate: true,
          deal: deal || { customer: customerForDeal },
          selectedFunnel,
          reloadDeals
        }
      }
    });
  }, [conversation, customerData, dispatch, reloadDeals]);

  useEffect(() => {
    // Carga negociaciones automáticamente al montar o cambiar customer
    if (!customerData || isEmpty(customerData)) return;
    reloadDeals();
  }, [customerData, reloadDeals]);

  /**
   * Retorna estados y acciones optimizados para CustomerFunnelsMC.
   * @function return
   * @memberof module:useCustomerFunnelsData
   * @returns {UseCustomerFunnelsDataReturn} Estados y acciones optimizados
   */
  return useMemo(() => ({
    customerDeals,
    loading,
    reloadDeals,
    openDealModal,
    regionalConfig
  }), [customerDeals, loading, reloadDeals, openDealModal, regionalConfig]);
}
